import { Point, Rectangle, Matrix } from "../Utils/Matrix";
import { TopoNode } from "../Topology/TopoNode";
import { List } from "../Utils/List";

export interface ILayoutCluster {
	Left: number;
	Right: number;
	Top: number;
	Bottom: number;
	Width: number;
	Height: number;
	Offset: (x: number, y: number) => void;
	CalculateSize: () => void;
	LeftMostLocation: () => Point;
	Rotate: (degree: number) => void;
}

export class LayoutCluster<T extends ILayoutCluster> implements ILayoutCluster
{
	private static NodeMargin = 10;

	public Left:number;
	public Right: number;
	public Top: number;
	public Bottom: number;
	public get Width(): number {
		return Math.abs(this.Right - this.Left);
	}

	public get Height(): number {
		return Math.abs(this.Bottom - this.Top);
	}

	public get CenterX(): number {
		return this.Left + (this.Width / 2);
	}

	public get CenterY(): number {
		return this.Top + (this.Height / 2);
	}

	public Root: TopoNode;
		
	private _clusters: List<T>;
	public get Clusters(): List<T> {
		return this._clusters;
	}

	constructor(root: TopoNode) {
		this.Root = root;
		this._clusters = new List<T>();
	}

	public AddCluster(cluster: T): void {
		this._clusters.Add(cluster);
	}

	public get RootSize(): Rectangle {
		var rect = new Rectangle();
		rect.X = this.Root.Left - this.Root.Width / 2 - LayoutCluster.NodeMargin;
		rect.Y = this.Root.Top - this.Root.Height / 2 - LayoutCluster.NodeMargin,
		rect.Width = this.Root.Width + LayoutCluster.NodeMargin * 2,
		rect.Height = this.Root.Height + LayoutCluster.NodeMargin * 2
		return rect;
	}

	public CalculateSize(): void {
		var rootSize: Rectangle = this.RootSize;
		this.Left = rootSize.X;
		this.Top = rootSize.Y;
		this.Right = rootSize.X + rootSize.Width;
		this.Bottom = rootSize.Y + rootSize.Height;

		this._clusters.ForEach((cluster) => {
			cluster.CalculateSize();

			if (cluster.Bottom > this.Bottom)
				this.Bottom = cluster.Bottom;

			if (cluster.Top < this.Top)
				this.Top = cluster.Top;

			if (cluster.Right > this.Right)
				this.Right = cluster.Right;

			if (cluster.Left < this.Left)
				this.Left = cluster.Left;
		});
	}

	public LeftMostLocation(): Point {
		var result: Point = this.Root.Location;

		this._clusters.ForEach((cluster) => {
			var clusterPoint: Point = cluster.LeftMostLocation();
			if (clusterPoint.X < result.X)
				result = clusterPoint;
		});

		return result;
	}

	public Offset(x: number, y: number): void {
		this.Root.X += x;
		this.Root.Y += y;

		this._clusters.ForEach((cluster) => {
			cluster.Offset(x, y);
		});
	}

	public Rotate(degrees: number) {
		LayoutCluster.RotateNode(this.Root, degrees);

		this._clusters.ForEach((cluster) => {
			cluster.Rotate(degrees);
		});
	}

	private static RotateNode(node: TopoNode, angle: number) {
		var m = new Matrix();
		m.Rotate(angle);

		var point = [ node.Location ];
		m.TransformPoints(point);
		node.Location = point[0];
	}
}
